export interface TextureWatch {
  stop: (() => void) | null;
  armed: boolean;
  frame?: string | number;
}

interface TextureBus {
  exists(key: string): boolean;
  on(event: string, fn: () => void): void;
  off(event: string, fn: () => void): void;
}

interface Bindable {
  scene: object | null;
  texture: { key: string };
  setTexture(key: string, frame?: string | number): unknown;
  setDisplaySize?(width: number, height: number): unknown;
  once(event: string, fn: () => void): void;
  displayLock?: [number, number];
}

const watches = new WeakMap<object, TextureWatch>();

type MissingPaint = (scene: object, key: string) => string | null;

let paintMissing: MissingPaint | null = null;

/** Boot installs a painter so a missing key never shows the green placeholder. */
export function setMissingTexturePaint(paint: MissingPaint | null): void {
  paintMissing = paint;
}

function watchFor(sprite: object): TextureWatch {
  let watch = watches.get(sprite);
  if (!watch) {
    watch = { stop: null, armed: false };
    watches.set(sprite, watch);
  }
  return watch;
}

function restoreDisplay(sprite: Bindable): void {
  const lock = sprite.displayLock;
  if (!lock || typeof sprite.setDisplaySize !== 'function') return;
  sprite.setDisplaySize(lock[0], lock[1]);
}

/**
 * Paint `key` now, or when the texture manager adds it.
 * A sprite created before its file arrives otherwise keeps the missing-image square.
 */
export function bindTexture(
  scene: { textures: TextureBus },
  sprite: Bindable,
  key: string,
  watch: TextureWatch = watchFor(sprite),
  frame?: string | number,
): void {
  watch.stop?.();
  watch.stop = null;
  watch.frame = frame;
  if (scene.textures.exists(key)) {
    if (sprite.texture.key !== key) {
      sprite.setTexture(key, frame);
      restoreDisplay(sprite);
    }
    return;
  }
  const event = `addtexture-${key}`;
  const onAdd = () => {
    watch.stop = null;
    scene.textures.off(event, onAdd);
    if (!sprite.scene || sprite.texture.key === key) return;
    sprite.setTexture(key, watch.frame);
    restoreDisplay(sprite);
  };
  watch.stop = () => scene.textures.off(event, onAdd);
  scene.textures.on(event, onAdd);
  if (!watch.armed) {
    watch.armed = true;
    sprite.once('destroy', () => {
      watch.stop?.();
      watch.stop = null;
    });
  }
}

let installed = false;

interface TextureProto {
  setTexture: (key: string, frame?: string | number, updateSize?: boolean, updateOrigin?: boolean) => unknown;
  setDisplaySize?: (width: number, height: number) => unknown;
}

/** Passed in so this module can load in Node without Phaser's browser globals. */
export interface TextureFactories {
  Image: { prototype: TextureProto };
  Sprite: { prototype: TextureProto };
  NineSlice: { prototype: TextureProto };
}

/** Images, sprites, and nine-slices rebind if their texture is still loading. */
export function installTextureBind(gameObjects: TextureFactories): void {
  if (installed) return;
  installed = true;
  patch(gameObjects.Image.prototype);
  patch(gameObjects.Sprite.prototype);
  patch(gameObjects.NineSlice.prototype);
}

function patch(proto: TextureProto): void {
  const original = proto.setTexture;
  proto.setTexture = function (
    this: Bindable & { scene: { textures: TextureBus } | null },
    key: string,
    frame?: string | number,
    updateSize?: boolean,
    updateOrigin?: boolean,
  ) {
    let drawKey = key;
    if (
      typeof key === 'string' &&
      key &&
      !key.startsWith('__') &&
      !key.startsWith('standin-') &&
      this.scene?.textures &&
      !this.scene.textures.exists(key) &&
      paintMissing
    ) {
      try {
        const stand = paintMissing(this.scene, key);
        if (stand && this.scene.textures.exists(stand)) drawKey = stand;
      } catch {
        drawKey = key;
      }
    }
    const result = original.call(this, drawKey, frame, updateSize, updateOrigin);
    if (typeof key === 'string' && this.scene?.textures) bindTexture(this.scene, this, key, watchFor(this), frame);
    restoreDisplay(this);
    return result;
  };
  if (!proto.setDisplaySize) return;
  const size = proto.setDisplaySize;
  proto.setDisplaySize = function (this: Bindable, width: number, height: number) {
    this.displayLock = [width, height];
    return size.call(this, width, height);
  };
}
